import { collection, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { db } from '../firebase/firebase';

/**
 * Collections available for export
 */
const COLLECTIONS = [
  {
    name: 'contacts',
    sheetName: 'Contacts',
    fileName: 'Contacts',
  },
  {
    name: 'members',
    sheetName: 'Members',
    fileName: 'Members',
  },
  {
    name: 'membershipApplications',
    sheetName: 'Membership Applications',
    fileName: 'Membership_Applications',
  },
  {
    name: 'merchOrders',
    sheetName: 'Merch Orders',
    fileName: 'Merch_Orders',
  },
  {
    name: 'partnershipInquiries',
    sheetName: 'Partnership Inquiries',
    fileName: 'Partnership_Inquiries',
  },
  {
    name: 'volunteerApplications',
    sheetName: 'Volunteer Applications',
    fileName: 'Volunteer_Applications',
  },
];

/**
 * Converts Firestore values into Excel-friendly values
 */
const formatValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  // Firestore Timestamp
  if (
    typeof value === 'object' &&
    typeof value.toDate === 'function'
  ) {
    return value.toDate().toLocaleString();
  }

  // JavaScript Date
  if (value instanceof Date) {
    return value.toLocaleString();
  }

  // Array
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'object' && item !== null) {
          return JSON.stringify(item);
        }

        return item;
      })
      .join(', ');
  }

  // Nested object
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return value;
};

/**
 * Formats a Firestore document
 */
const formatDocument = (doc) => {
  const data = doc.data();

  const formattedData = {
    ID: doc.id,
  };

  Object.entries(data).forEach(([key, value]) => {
    formattedData[key] = formatValue(value);
  });

  return formattedData;
};

/**
 * Fetch data from a Firestore collection
 */
const getCollectionData = async (collectionName) => {
  const querySnapshot = await getDocs(
    collection(db, collectionName)
  );

  return querySnapshot.docs.map((doc) =>
    formatDocument(doc)
  );
};

/**
 * Creates and downloads an Excel workbook
 */
const downloadExcelFile = (
  data,
  sheetName,
  fileName
) => {
  const workbook = XLSX.utils.book_new();

  let worksheet;

  if (data.length > 0) {
    worksheet = XLSX.utils.json_to_sheet(data);
  } else {
    worksheet = XLSX.utils.aoa_to_sheet([
      ['No data available'],
    ]);
  }

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    sheetName.substring(0, 31)
  );

  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  const blob = new Blob(
    [excelBuffer],
    {
      type:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    }
  );

  const date = new Date()
    .toISOString()
    .split('T')[0];

  saveAs(
    blob,
    `${fileName}_Export_${date}.xlsx`
  );
};

/**
 * Export ONE specific Firestore collection
 *
 * Example:
 * exportFirestoreCollection('members')
 */
export const exportFirestoreCollection = async (
  collectionName
) => {
  const collectionConfig = COLLECTIONS.find(
    (item) => item.name === collectionName
  );

  if (!collectionConfig) {
    throw new Error(
      `Collection "${collectionName}" is not configured for export.`
    );
  }

  const data = await getCollectionData(collectionName);

  downloadExcelFile(
    data,
    collectionConfig.sheetName,
    collectionConfig.fileName
  );

  return {
    collectionName,
    count: data.length,
  };
};

/**
 * Export all configured Firestore collections
 * into one Excel workbook with multiple sheets
 */
export const exportAllFirestoreData = async () => {
  const workbook = XLSX.utils.book_new();

  for (const collectionConfig of COLLECTIONS) {
    const { name, sheetName } = collectionConfig;

    try {
      const data = await getCollectionData(name);

      let worksheet;

      if (data.length > 0) {
        worksheet = XLSX.utils.json_to_sheet(data);
      } else {
        worksheet = XLSX.utils.aoa_to_sheet([
          ['No data available'],
        ]);
      }

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        sheetName.substring(0, 31)
      );
    } catch (error) {
      console.error(
        `Error exporting collection "${name}":`,
        error
      );

      const worksheet = XLSX.utils.aoa_to_sheet([
        ['Error exporting collection'],
        [error.message || 'Unknown error'],
      ]);

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        sheetName.substring(0, 31)
      );
    }
  }

  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  const blob = new Blob(
    [excelBuffer],
    {
      type:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    }
  );

  const date = new Date()
    .toISOString()
    .split('T')[0];

  saveAs(
    blob,
    `Company_Data_Export_${date}.xlsx`
  );
};